import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import AppBar from '@material-ui/core/AppBar';
import { colors } from '../../constants/colors';
import styled, { css } from 'styled-components';
import { safeGet } from '../../core/util/object';
import { getProfileColorTheme } from '../Account/util/colors';
import LoggedOutMenu from './components/LoggedOutMenu';
import LoggedInMenu from './components/LoggedInMenu';
import Logo from '../SolvPatternLibrary/Logo';
import { LinkButton } from '../SolvPatternLibrary/Button/BaseButton';
import Button from '@material-ui/core/Button';
import { analyticsTrackEvent } from '../../core/analytics';
import { fontFamily, fontSize } from '../../constants/text';
import DesktopTabs from './components/DesktopTabs';
import { HEADER_V2_CLICK_FAQ, HEADER_V2_CLICK_LOGO } from '../../core/analytics/events';
import { BASE_URL } from '../../config';
import { sizes, breakpoints } from '../../core/util/styledComponents';
import { FAQ_PATH } from '../../constants';
import Hidden from '@material-ui/core/Hidden';
import { getDefaultBreakpoint } from '../../core/util/device/screen';
import history from '../../core/history';

const StyledAppBar = styled(AppBar)`
  width: 100%;
  background-color: ${colors.whiteGrey};
  border: 0;
  box-sizing: border-box;
  ${({ scrolled, noborder }) => css`
    box-shadow: ${scrolled ? '0 3px 6px 0 rgba(0,0,0,0.2)' : 'unset'};
    border-bottom: ${scrolled || noborder ? '0' : `1px solid ${colors.dorianGrey}`};
  `}
`;

const Container = styled.div`
  height: 100px;
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  flex-direction: row;
  box-sizing: border-box;
  padding: 0 32px;
  width: 100%;
  max-width: ${breakpoints.lg};
  margin: 0 auto;
`;

const LogoContainer = styled(Logo)`
  flex-grow: 0;
  height: 100px;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding-right: 30px;

  img {
    height: 30px;
  }
`;

const TabsContainer = styled.div`
  flex-grow: 1;
  display: flex;
  align-items: flex-start;
`;

const RightContainer = styled.div`
  flex-grow: 0;
  height: 100px;
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const StyledOutlinedButton = styled(Button)`
  margin-top: 15px;
  padding: 0;
  text-transform: capitalize;
  color: ${colors.blueMonday};
  border: 1px solid ${colors.dorianGrey};
  font-size: 14px;
  line-height: 21px;
  text-align: center;
  height: 35px;
  width: 77px;
  font-weight: normal;
  font-family: ${fontFamily.book};
  ${props => css`
    border-left-width: ${props.side === 'right' ? '0' : '1px'};
    border-radius: ${props.side === 'right' ? '0 4px 4px 0' : '4px 0 0 4px'};
  `}
`;

const StyledLinkButton = styled(LinkButton)`
  color: ${colors.blueMonday};
  margin-top: 15px;
  font-size: ${fontSize.mediumSmall};
  line-height: 21px;
  height: 35px;
  & :hover {
    background-color: transparent;
  }
  text-transform: none;
`;

class Desktop extends Component {
  static propTypes = {
    accountSummary: PropTypes.object,
    bottomElement: PropTypes.node,
    handleLogout: PropTypes.func,
    isLoggedIn: PropTypes.bool.isRequired,
    isScrolled: PropTypes.bool,
    logoOnly: PropTypes.bool,
    openLogInModal: PropTypes.func,
    openSignUpModal: PropTypes.func,
    selectedTabId: PropTypes.string,
    showModal: PropTypes.func,
    tabProperties: PropTypes.array.isRequired,
    userProfile: PropTypes.object,
    noBorder: PropTypes.bool,
  };

  getLoggedInRightElement = () => {
    const { accountSummary, userProfile, handleLogout } = this.props;
    const userProfileId = safeGet(userProfile)('user_profile_id');

    return (
      <LoggedInMenu
        accountSummary={accountSummary}
        currentProfile={userProfile}
        profileColor={getProfileColorTheme(accountSummary, userProfileId)}
        handleLogout={handleLogout}
      />
    );
  };

  getLoggedOutRightElement = () => {
    const { openLogInModal, openSignUpModal } = this.props;
    return (
      <Fragment>
        <Hidden smDown initialWidth={getDefaultBreakpoint()}>
          <StyledLinkButton disableRipple onClick={this.handleClickOnFAQ}>
            Help
          </StyledLinkButton>
          <StyledOutlinedButton
            variant="outlined"
            data-testid="login-header-button"
            side="left"
            onClick={openLogInModal}
          >
            Log in
          </StyledOutlinedButton>
          <StyledOutlinedButton
            variant="outlined"
            data-testid="signup-header-button"
            side="right"
            onClick={openSignUpModal}
          >
            Sign Up
          </StyledOutlinedButton>
        </Hidden>
        <Hidden mdUp initialWidth={getDefaultBreakpoint()}>
          <LoggedOutMenu openLogInModal={openLogInModal} openSignUpModal={openSignUpModal} />
        </Hidden>
      </Fragment>
    );
  };

  getRightElement = () => {
    if (this.props.isLoggedIn) {
      return this.getLoggedInRightElement();
    }

    return this.getLoggedOutRightElement();
  };

  handleClickOnFAQ = () => {
    analyticsTrackEvent(HEADER_V2_CLICK_FAQ, { isMobile: false });
    window.location.href = `${BASE_URL}${FAQ_PATH}`;
  };

  handleLogoClick = () => {
    analyticsTrackEvent(HEADER_V2_CLICK_LOGO, { isMobile: false });

    const { isLoggedIn, userProfile } = this.props;
    if (userProfile) {
      history.push(`/account/people/${userProfile.user_profile_id}/get-care`);
    } else if (isLoggedIn) {
      history.push('/account/people/');
    } else {
      history.push('/');
    }
  };

  render() {
    const {
      tabProperties,
      selectedTabId,
      isScrolled,
      bottomElement,
      logoOnly,
      noBorder,
    } = this.props;

    return (
      <StyledAppBar
        position="sticky"
        scrolled={isScrolled ? 1 : 0}
        noborder={noBorder ? 1 : 0} // https://github.com/styled-components/styled-components/issues/1198
      >
        <Container>
          <LogoContainer handleLogoOnClick={this.handleLogoClick} />
          <TabsContainer>
            {!logoOnly && (
              <DesktopTabs tabProperties={tabProperties} selectedTabId={selectedTabId} />
            )}
          </TabsContainer>
          <RightContainer>{!logoOnly && <div>{this.getRightElement()}</div>}</RightContainer>
        </Container>
        {bottomElement}
      </StyledAppBar>
    );
  }
}

export default Desktop;
